import { select, call } from 'typed-redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';
import { put } from 'redux-saga/effects';

import { xcriticalModalOpen } from '@xcritical/modal';
import { IItem } from '@xcritical/grid';

import { confirmModalSaga } from '@crm-framework/confirm';
import { registryActions } from '@crm-framework/registry';
import {
  crmGridInit,
  crmGridSetItems,
  gridSelectedRowsSelector,
} from '@crm-framework/grid';

import { getDictionaryByNameSelector } from '@crm-package/dictionaries';

import {
  GROUP_IMPORT_COLUMNS,
  GROUP_IMPORT_CONFIRM_MODAL,
  GROUP_IMPORT_GRID,
  GROUP_IMPORT_MODAL,
  GROUP_OPERATION_REGISTRY,
} from '../components';

import { apiSendIdsForGroupImport } from './api';
import { handleChatError } from './helpers';
import { getToken } from './utils';

export function* handleGroupContactImport({
  payload: { gridName },
}: PayloadAction<{ gridName: string }>) {
  try {
    const token = yield* getToken();

    const selectedRows: IItem[] = yield* select(
      gridSelectedRowsSelector,
      gridName
    );

    if (selectedRows.length > 2000) {
      yield* confirmModalSaga(GROUP_IMPORT_CONFIRM_MODAL, {
        confirmMessage: 'You can import a maximum of 2,000 leads at a time.',
      });

      return;
    }

    const businessUnits = yield* select(
      getDictionaryByNameSelector,
      'businessUnit'
    );

    const countedByEnv = {};

    const mappedRows = selectedRows.map((item) => {
      const envId =
        businessUnits.find((bu) => bu.id === item.businessUnitId)
          ?.environmentId ?? null;
      countedByEnv[envId] = (countedByEnv[envId] ?? 0) + 1;

      return {
        clientId: item.id,
        envId,
      };
    });

    const uniqueEnvironments = Object.keys(countedByEnv);

    if (Object.values(countedByEnv).some((count: number) => count > 500)) {
      const environments = yield* select(
        getDictionaryByNameSelector,
        'environment'
      );

      const countedByEnvWithEnvName = uniqueEnvironments.map((id) => ({
        count: countedByEnv[id],
        envName:
          environments.find((env) => `${env.id}` === id)?.friendlyName ?? '',
      }));

      const underlimitedEnv = countedByEnvWithEnvName
        .filter((env) => env.count > 500)
        .reduce((str, env) => `${str}${env.envName} (${env.count}), `, '')
        .slice(0, -2);
      yield* confirmModalSaga(GROUP_IMPORT_CONFIRM_MODAL, {
        confirmMessage: `You can import a maximum of 500 leads in one environment at a time.
        This environments: <b>${underlimitedEnv}</b> exceeds the permissible limit`,
      });

      return;
    }

    const isAcceptConfirm = yield* confirmModalSaga(
      GROUP_IMPORT_CONFIRM_MODAL,
      {
        confirmMessage: `Are you sure you want to import ${selectedRows.length} clients to telegram accounts?`,
      }
    );

    if (token && isAcceptConfirm) {
      yield put(registryActions.set(GROUP_OPERATION_REGISTRY, true));
      yield put(xcriticalModalOpen(GROUP_IMPORT_MODAL));
      yield put(
        crmGridInit(GROUP_IMPORT_GRID, {
          columns: GROUP_IMPORT_COLUMNS,
          items: [],
        })
      );

      const response = yield* call(
        apiSendIdsForGroupImport,
        mappedRows,
        uniqueEnvironments.map((env) => parseInt(env, 10)),
        token
      );

      yield put(crmGridSetItems(GROUP_IMPORT_GRID, response));
    }
  } catch (e) {
    yield* handleChatError(e);
  } finally {
    yield put(registryActions.set(GROUP_OPERATION_REGISTRY, false));
  }
}
